import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthStore } from '@store/auth.store';
import { environment } from '@envs/environment';

// Error reasons that come back as 401/403 but don't mean the session itself is
// invalid (e.g. a document-upload endpoint rejecting the request for its own
// unrelated reasons, or the admin's roles lacking a permission). Logging the
// admin out for these is wrong — surface the error to the caller instead and
// leave the session alone.
const NON_SESSION_ERROR_REASONS = new Set([
  'unverified_accesskey',
  'missing_accesskey',
  'missing_permission',
]);

/**
 * Attaches the bearer token to API requests that don't already carry an
 * Authorization header (existing services that set it manually keep working),
 * and forces a logout + redirect on a 401/403 that actually indicates the
 * session/token is invalid.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const isApiRequest =
    req.url.startsWith(environment.apiUrl) ||
    req.url.startsWith(environment.apiUrl_okito_official);

  const token = authStore.currentState.token;

  const request =
    isApiRequest && token && !req.headers.has('Authorization')
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(request).pipe(
    catchError((error: unknown) => {
      const isAuthStatus =
        error instanceof HttpErrorResponse &&
        (error.status === 401 || error.status === 403);
      const reason = isAuthStatus ? (error as HttpErrorResponse).error?.reason : undefined;
      const isAuthError = isAuthStatus && !NON_SESSION_ERROR_REASONS.has(reason);

      // Don't logout/redirect while on the login page itself (e.g. a failed
      // login or OTP attempt), otherwise the mid-OTP token gets wiped
      if (isAuthError && !router.url.startsWith('/admin/login')) {
        authStore.logout().subscribe(() => {
          router.navigate(['/admin/login']);
        });
      }
      return throwError(() => error);
    })
  );
};
